/**
 * Загрузчик карточки сотрудника по id через scope api: показывает индикатор/ошибку и рендерит
 * EmployeeCardView. Используется и у клиента (роут /staff/:id), и у команды (inline в карточке
 * клиента). Оркестрирует правку сотрудника (§9.4 «Изменить информацию о сотруднике»): при входе
 * в режим правки подгружает справочник мест + сотрудников (для формы/сменщиков) и рендерит
 * обобщённую CreateEmployeeForm в режиме edit; после сохранения перезагружает карточку.
 * Аналогично оркестрирует «Добавить историческое обучение» (§11.1, p2-manual-historical/IC-4):
 * по клику лениво грузит каталог программ (`trainingApi.listPrograms`, по образцу `startEdit`),
 * показывает форму в EmployeeCardView; после сохранения — `reloadToken` (та же схема, что и у
 * правки сотрудника). Историческая СОУТ/профриски (§1.1/§1.2, p3-structure-sout-training,
 * code-review волны 2): панели `HistoricalSoutPanel`/`HistoricalProfriskPanel` сами управляют
 * своим открытием/списком — сюда пробрасывается только `onSaved` → тот же `reloadToken`-бамп,
 * чтобы сводки «Условия труда»/«Информация по профрискам» в EmployeeCardView подхватили новую
 * запись без ручной перезагрузки страницы.
 */
import { useEffect, useState } from 'react'
import { ArrowLeft, Trash2 } from 'lucide-react'
import {
  trainingApi,
  type CompanyScopeApi,
  type Department,
  type Employee,
  type EmployeeCard,
  type Program,
  type TrainingDirectionItem,
  type TrainingRecord,
  type Workplace,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'
import { Card } from '@/design-system/components/layout/card'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { EmployeeCardView } from './employee-card'
import { CreateEmployeeForm } from './employees-manager'

export function EmployeeCardLoader({
  api,
  id,
  onBack,
  onOpenWorkplace,
  manageDocuments,
  onTrainEmployee,
  trainEmployeeBusy,
}: {
  api: CompanyScopeApi
  id: string
  onBack: () => void
  /** Проброс в EmployeeCardView (p2-ot-section §9.5, design D5/D11) — см. там же. */
  onOpenWorkplace?: (workplaceId: string) => void
  /**
   * Разрешить управление документами сотрудника в карточке — документом об образовании
   * (centrika-docs!59) и согласием на обработку ПД — только у клиента (эндпоинты загрузки/удаления
   * клиентские). У команды опущено — документы только на просмотр/скачивание.
   */
  manageDocuments?: boolean
  /** «Обучить сотрудника» (training-order-entry-points) — только у клиента; см. EmployeeCardView. */
  onTrainEmployee?: (employee: { id: string; fullName: string }) => void
  trainEmployeeBusy?: boolean
}) {
  const [employee, setEmployee] = useState<EmployeeCard | null>(null)
  // Дерево разделов каталога — «Обучения» по разделам, как в каталоге (ответ заказчицы 04.11).
  // Не загрузилось — записи одним списком в «Прочее», карточка не ломается.
  const [directions, setDirections] = useState<TrainingDirectionItem[]>([])
  useEffect(() => {
    const controller = new AbortController()
    trainingApi
      .listDirections(controller.signal)
      .then((d) => {
        if (!controller.signal.aborted) setDirections(d)
      })
      .catch(() => undefined)
    return () => controller.abort()
  }, [])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  // Правка (§9.4): editCtx — подгруженные справочники для формы; reloadToken перезапускает
  // загрузку карточки после сохранения (переиспользует эффект ниже вместо ручного перезапроса).
  const [editing, setEditing] = useState(false)
  const [editCtx, setEditCtx] = useState<{
    workplaces: Workplace[]
    employees: Employee[]
    departments: Department[]
  } | null>(null)
  const [editLoading, setEditLoading] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)
  // Историческое обучение (§11.1): addingTraining — показать форму вместо кнопки; programs —
  // каталог, загружается лениво по клику (по образцу editCtx/startEdit).
  const [addingTraining, setAddingTraining] = useState(false)
  const [trainingPrograms, setTrainingPrograms] = useState<Program[] | null>(null)
  const [trainingLoading, setTrainingLoading] = useState(false)
  // Правка и удаление исторической записи обучения: editingTrainingId — запись, открытая на
  // правку (форма на месте строки); confirmDeleteTraining — запись в диалоге подтверждения.
  const [editingTrainingId, setEditingTrainingId] = useState<string | null>(null)
  const [confirmDeleteTraining, setConfirmDeleteTraining] = useState<TrainingRecord | null>(null)
  const [deletingTraining, setDeletingTraining] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    api
      .getEmployee(id, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setEmployee(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить карточку'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [api, id, reloadToken])

  // Деактивация/активация (§10.3, design D1) — мягкая, обратимая; история сохраняется.
  async function toggleActive() {
    if (!employee) return
    setActionError(null)
    setToggling(true)
    try {
      const updated = employee.isActive
        ? await api.deleteEmployee(id)
        : await api.activateEmployee(id)
      setEmployee((prev) => (prev ? { ...prev, ...updated } : prev))
    } catch (err) {
      setActionError(
        getErrorMessage(
          err,
          employee.isActive ? 'Не удалось деактивировать' : 'Не удалось активировать',
        ),
      )
    } finally {
      setToggling(false)
    }
  }

  // Вход в режим правки (§9.4): подгружаем места и сотрудников (для формы/выбора сменщиков),
  // затем показываем форму. Справочники нужны только при правке — не грузим их на каждый просмотр.
  async function startEdit() {
    setActionError(null)
    setEditLoading(true)
    try {
      const [wps, emps, deps] = await Promise.all([
        api.listWorkplaces(),
        api.listEmployees(),
        api.listDepartments(),
      ])
      setEditCtx({ workplaces: wps.items, employees: emps.items, departments: deps.items })
      setEditing(true)
    } catch (err) {
      setActionError(getErrorMessage(err, 'Не удалось открыть форму правки'))
    } finally {
      setEditLoading(false)
    }
  }

  function closeEdit() {
    setEditing(false)
    setEditCtx(null)
  }

  function onSaved() {
    closeEdit()
    setReloadToken((t) => t + 1) // перезагрузить карточку (updateEmployee отдаёт Employee, не EmployeeCard)
  }

  // Вход в добавление исторического обучения (§11.1): грузим каталог программ один раз перед
  // показом формы (createRecord/updateRecord этого не отдают — программа нужна для селекта).
  async function startAddTraining() {
    setActionError(null)
    setEditingTrainingId(null)
    setTrainingLoading(true)
    try {
      const programs = await trainingApi.listPrograms()
      setTrainingPrograms(programs)
      setAddingTraining(true)
    } catch (err) {
      setActionError(getErrorMessage(err, 'Не удалось загрузить каталог программ'))
    } finally {
      setTrainingLoading(false)
    }
  }

  function closeAddTraining() {
    setAddingTraining(false)
    setEditingTrainingId(null)
  }

  function onTrainingSaved() {
    setAddingTraining(false)
    setEditingTrainingId(null)
    setReloadToken((t) => t + 1) // перезагрузить карточку — блок «Обучения» подхватит новую запись
  }

  // Правка исторической записи: каталог программ нужен форме так же, как при добавлении, —
  // грузится один раз и переиспользуется. Одновременно открыта одна форма (добавление или правка).
  async function startEditTraining(record: TrainingRecord) {
    setActionError(null)
    setAddingTraining(false)
    if (!trainingPrograms) {
      setTrainingLoading(true)
      try {
        setTrainingPrograms(await trainingApi.listPrograms())
      } catch (err) {
        setActionError(getErrorMessage(err, 'Не удалось загрузить каталог программ'))
        return
      } finally {
        setTrainingLoading(false)
      }
    }
    setEditingTrainingId(record.id)
  }

  /** Подтверждение в диалоге: закрываем диалог и удаляем запись, затем перезагружаем карточку. */
  async function confirmAndRemoveTraining() {
    if (!confirmDeleteTraining) return
    const target = confirmDeleteTraining
    setConfirmDeleteTraining(null)
    setActionError(null)
    setDeletingTraining(true)
    try {
      await trainingApi.removeRecord(target.id)
      if (editingTrainingId === target.id) setEditingTrainingId(null)
      setReloadToken((t) => t + 1)
    } catch (err) {
      setActionError(getErrorMessage(err, 'Не удалось удалить историческую запись'))
    } finally {
      setDeletingTraining(false)
    }
  }

  // Историческая СОУТ/профриски (§1.1/§1.2, code-review волны 2): панели сами закрывать себя не
  // просят (остаются открытыми со своим списком) — только бампаем reloadToken, чтобы сводки
  // «Условия труда»/«Информация по профрискам» получили свежий getEmployee.
  function onSoutHistoricalSaved() {
    setReloadToken((t) => t + 1)
  }

  function onProfriskHistoricalSaved() {
    setReloadToken((t) => t + 1)
  }

  const backButton = (
    <Button
      variant="secondary"
      size="sm"
      onClick={onBack}
      leftIcon={<ArrowLeft className="size-4 stroke-current" aria-hidden />}
    >
      Назад
    </Button>
  )
  // В карточке «Назад» — в её верхней строке, рядом с ФИО и действиями (ответ заказчицы 04.11);
  // пока карточки нет (загрузка, ошибка, правка) — отдельной строкой.
  const cardShown = Boolean(employee) && !loading && !error && !(editing && editCtx)

  return (
    <div>
      {cardShown ? null : <div className="mb-4">{backButton}</div>}
      {loading ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : employee ? (
        editing && editCtx ? (
          <CreateEmployeeForm
            api={api}
            workplaces={editCtx.workplaces}
            employees={editCtx.employees}
            departments={editCtx.departments}
            initial={employee}
            onCreated={onSaved}
            onCancel={closeEdit}
          />
        ) : (
          <>
            {actionError ? (
              <Alert variant="danger" className="mb-4">
                {actionError}
              </Alert>
            ) : null}
            <EmployeeCardView
              employee={employee}
              backButton={backButton}
              directions={directions}
              onToggleActive={toggleActive}
              toggling={toggling}
              onEdit={editLoading ? undefined : () => void startEdit()}
              onOpenWorkplace={onOpenWorkplace}
              onAddHistoricalTraining={() => void startAddTraining()}
              addingHistoricalTrainingLoading={trainingLoading}
              addingHistoricalTraining={addingTraining}
              historicalTrainingPrograms={trainingPrograms ?? undefined}
              onHistoricalTrainingSaved={onTrainingSaved}
              onCancelHistoricalTraining={closeAddTraining}
              onEditHistoricalTraining={(record) => void startEditTraining(record)}
              onDeleteHistoricalTraining={setConfirmDeleteTraining}
              editingHistoricalTrainingId={editingTrainingId ?? undefined}
              historicalTrainingActionsDisabled={trainingLoading || deletingTraining}
              onSoutHistoricalSaved={onSoutHistoricalSaved}
              onProfriskHistoricalSaved={onProfriskHistoricalSaved}
              educationDocumentApi={manageDocuments ? api : undefined}
              onEducationDocumentChanged={
                manageDocuments
                  ? (doc) =>
                      setEmployee((prev) => (prev ? { ...prev, educationDocument: doc } : prev))
                  : undefined
              }
              onTrainEmployee={
                onTrainEmployee
                  ? () => onTrainEmployee({ id: employee.id, fullName: employee.fullName })
                  : undefined
              }
              trainEmployeeBusy={trainEmployeeBusy}
              consentApi={manageDocuments ? api : undefined}
              onConsentChanged={
                manageDocuments
                  ? (consent) => setEmployee((prev) => (prev ? { ...prev, consent } : prev))
                  : undefined
              }
            />
          </>
        )
      ) : null}

      <Dialog
        open={confirmDeleteTraining !== null}
        onOpenChange={(next) => {
          if (!next) setConfirmDeleteTraining(null)
        }}
      >
        {confirmDeleteTraining ? (
          <DialogContent
            title="Удалить историческое обучение?"
            description={`Запись «${confirmDeleteTraining.programName}» будет удалена, сроки обучения сотрудника пересчитаются.`}
            className="max-w-sm"
          >
            <Card header="Удалить историческое обучение?">
              <p className="text-txt-secondary">
                Запись «{confirmDeleteTraining.programName}» будет удалена, сроки обучения
                сотрудника пересчитаются. Действие необратимо.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  className="border-red bg-red text-white hover:opacity-90"
                  onClick={() => void confirmAndRemoveTraining()}
                  leftIcon={<Trash2 className="size-4 stroke-current" aria-hidden />}
                >
                  Удалить
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setConfirmDeleteTraining(null)}>
                  Отмена
                </Button>
              </div>
            </Card>
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  )
}
